import { Suspense, useEffect, useState } from "react";
import type { Locale } from "../i18n/texts";
import type { LegalDocumentId } from "../legal/legalDocuments";
import { lazyScreen } from "../ui/lazyScreen";

// Los enlaces a las páginas legales llegan aparte (fuera del paquete de entrada); en las pantallas de ingreso y en
// /legal su código se pide al arrancar, así que aparecen junto con la página.
const LegalLinks = lazyScreen(
  () => import("../legal/LegalLinks").then((module) => module.LegalLinks),
  /^\/(login|loginmfa|recuperar|register|cambiarpassword|invitacion|soporte|empresa|legal)(\/|$)/,
);

/**
 * Pie legal de las pantallas de ingreso (decisión del dueño, 2026-11-02, para la verificación de empresa en Meta): la
 * línea la arma el servidor desde appsettings `LegalNotice` en el idioma de la persona. Oculta (204) o sin respuesta,
 * no se dibuja la línea y el ingreso sigue igual. Debajo van siempre los enlaces a las tres páginas legales públicas
 * (B3); mientras llega su código se reserva su alto para que nada se mueva.
 */
export function LegalNotice({ locale, current }: { locale?: Locale; current?: LegalDocumentId }) {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    fetch("/api/portal/legal-notice", { credentials: "same-origin", signal: abort.signal })
      .then((response) => (response.status === 200 ? (response.json() as Promise<{ text?: string }>) : null))
      .then((body) => setText(body?.text?.trim() || null))
      .catch(() => undefined);
    return () => abort.abort();
  }, []);
  return (
    <footer className="auth-legal" data-part="pie-legal">
      {text ? (
        <p className="auth-legal-line" data-part="pie-legal-linea">
          {text}
        </p>
      ) : null}
      <Suspense fallback={<div className="auth-legal-links auth-legal-links-reserva" aria-hidden="true" />}>
        <LegalLinks locale={locale} current={current} />
      </Suspense>
    </footer>
  );
}
